<template>
  <CForm @submit="submit">
    <CModal :title="$t('tireSettings.title')" @close="close" class="c-axle-settings-modal">
      <template #body>
        <h3>{{ $t('axleSettingsModal.title') }}</h3>
        <CCard>
          <div v-for="axle in axles" class="c-axle-settings-modal-axle">
            <COptionsDropdown :options="[AxleLocation.FRONT, AxleLocation.REAR]" :modelValue="axle.location"
                              @update:modelValue="l => setLocation(axle, l)" :title="getLocationLabel(axle)"
                              :getLabel="o => $t(o === AxleLocation.FRONT ? 'tireModal.frontAxle' : 'tireModal.rearAxle')"
                              :disabled="getAxleDisabledState(axle) || getHighestIndex(axle.location) === 0"/>

            <COptionsDropdown :options="[1, 2, 4, 6]" v-model="axle.wheelCount"
                              :getLabel="count => i18n.t('tireModal.wheelCount', { count })" :disabled="getAxleDisabledState(axle)"/>

            <COptionsDropdown :options="enumValues(BrakeType)" v-model="axle.brakeType" :getLabel="o => getBrakeTypeLabel(o)"
                              :disabled="getAxleDisabledState(axle)"/>

            <CButton theme="normal-danger" @click="remove(axle)" :disabled="getAxleDeleteDisabledState(axle)">
              <CIcon name="delete-trash"/>
            </CButton>
          </div>
          <CButton :disabled="axles.length > 9" @click="addAxle()" theme="ghost-primary" class="u-mx-4 u-my-2">
            <CIcon name="plus"/>
            {{ $t('axleSettingsModal.addAxle') }}
          </CButton>
        </CCard>

        <h3 class="u-mt-5">{{ $t('axleSettingsModal.spares') }}</h3>
        <CCard>
          <div v-for="spare in spares" class="c-axle-settings-modal-axle">
            <p style="grid-column-start: span 3">
              {{ $t('axleSettingsModal.extraSpareWheel', { count: spare.index + 1 }) }}
            </p>
            <CButton theme="normal-danger" @click="remove(spare)">
              <CIcon name="delete-trash"/>
            </CButton>
          </div>
          <CButton @click="addAxle(true)" theme="ghost-primary" class="u-mx-4 u-my-2">
            <CIcon name="plus"/>
            {{ $t('axleSettingsModal.addSpare') }}
          </CButton>
        </CCard>
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { orderBy, partition } from 'lodash';
  import { computed, reactive, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import COptionsDropdown from '@/dossier/components/COptionsDropdown.vue';
  import CModal from '@/general/components/CModal.vue';
  import Modals from '@/general/services/Modals';
  import { BasicModalResult, MODAL_CANCELLED, MODAL_CONFIRMED } from '@/general/utils/Modal';
  import CCard from '@/shared/components/CCard.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import { enumValues, isPresent } from '@/shared/utils/typescript';
  import AxleLocation from '../constants/AxleLocation';
  import BrakeType from '../constants/BrakeType';
  import { AxleData } from '../converters/tireConverters';
  import TireContext from '../models/TireContext';
  import AxleService from '../services/AxleService';

  const i18n = useI18n();

  const props = defineProps<{
    context: TireContext;
  }>();

  const emit = defineEmits<{ (e: 'close', v: BasicModalResult): void }>();

  const formData = reactive(formatAxlesFormData());

  watch(formData, () => {
    recalculateIndices();
  }, { deep: true });

  const sortedFormData = computed(() => orderBy(formData, ['location', 'index']));
  const axles = computed(() => sortedFormData.value.filter(a => a.location !== AxleLocation.SPARE));
  const spares = computed(() => sortedFormData.value.filter(a => a.location === AxleLocation.SPARE));

  function formatAxlesFormData() {
    const data = props.context.container.axles.map(a => {
      return {
        id: a.id,
        location: a.location,
        index: a.index,
        wheelCount: a.positions.length,
        brakeType: a.brakeType,
      } as AxleData;
    });
    return orderBy(data, ['location', 'index']);
  }

  function recalculateIndices() {
    const [frontAxles, rearAxles] = partition(axles.value, (a) => a.location === AxleLocation.FRONT);
    frontAxles.forEach((a, i) => a.index = i);
    rearAxles.forEach((a, i) => a.index = i);
    spares.value.forEach((a, i) => a.index = i);
  }

  function getLocationLabel(axle: AxleData) {
    const location = axle.location;
    if (axles.value.filter(a => a.location === location).length > 1) {
      return i18n.t(location === AxleLocation.FRONT ? 'tireModal.extraFrontAxle' : 'tireModal.extraRearAxle', { index: axle.index + 1 });
    } else {
      return i18n.t(location === AxleLocation.FRONT ? 'tireModal.frontAxle' : 'tireModal.rearAxle');
    }
  }

  function getHighestIndex(location: AxleLocation) {
    return Math.max(...formData.filter(a => a.location === location).map(a => a.index));
  }

  function getBrakeTypeLabel(brakeType: BrakeType) {
    switch (brakeType) {
      case BrakeType.DISC:
        return i18n.t('tireModal.discBrakes');
      case BrakeType.DRUM:
        return i18n.t('tireModal.drumBrakes');
      default:
        return i18n.t('tireModal.noBrakes');
    }
  }

  function getAxleDisabledState(axle: AxleData): boolean {
    return isPresent(axle.id);
  }

  function getAxleDeleteDisabledState(axle: AxleData): boolean {
    return axle.index === 0;
  }

  function setLocation(axle: AxleData, location: AxleLocation) {
    if (axle.location === location) return;
    axle.location = location;
    axle.index = getHighestIndex(location) + 1;
  }

  function addAxle(isSpare?: boolean) {
    formData.push({
      id: null,
      location: isSpare ? AxleLocation.SPARE : AxleLocation.REAR,
      index: getHighestIndex(isSpare ? AxleLocation.SPARE : AxleLocation.REAR) + 1,
      wheelCount: isSpare ? 1 : 2,
      brakeType: isSpare ? BrakeType.NO_BRAKES : BrakeType.DISC,
    });
  }

  async function remove(axle: AxleData) {
    if (isPresent(axle.id)) {
      await Modals.confirmDelete(i18n.t('axleSettingsModal.confirmDelete'));
      await AxleService.removeAxle(props.context, axle.id);
    }
    const index = formData.indexOf(axle);
    formData.splice(index, 1);
    recalculateIndices();
  }

  async function submit() {
    const addedAxles = formData.filter(a => !isPresent(a.id));
    if (addedAxles.length === 0) close();
    else {
      await AxleService.updateAxleLayout(props.context, addedAxles);
      emit('close', MODAL_CONFIRMED);
    }
  }

  function close() {
    emit('close', MODAL_CANCELLED);
  }
</script>

<style lang="scss">
  .c-axle-settings-modal {
    .c-card:not(.c-dropdown-panel) {
      padding: 0;
    }

    &-axle {
      display: grid;
      grid-template-columns: 3fr 2fr 2fr auto;
      gap: 1rem;
      align-items: center;
      padding: 1rem;
      border-bottom: 1px solid $border-color;

      .c-dropdown-toggle {
        width: 100%;
      }
    }
  }
</style>